import React, { useState, useEffect, ComponentProps } from 'react';
import { Input } from 'antd';
import styled from '@emotion/styled';

interface MFAInputProps extends ComponentProps<typeof Input> {
  codeLength: number;
  onCodeChanged: (code: string) => void;
  validated: boolean;
  clearUserInput: boolean;
  disabled?: boolean;
  showState?: boolean;
  resetErrorDisplay?: () => void;
  resendButton?: React.ReactNode;
}

type GoodBadOrUnknown = 'good' | 'bad' | 'unknown';

const Suffix = styled.div`
  border-radius: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 20px;
  height: 20px;
  color: white;
  background-color: ${({ color }) => color};
`;

const statusMap = new Map<GoodBadOrUnknown, { color: string; character: string }>([
  ['good', { color: 'green', character: '✓' }],
  ['bad', { color: 'red', character: '✗' }],
  ['unknown', { color: '', character: '' }]
]);

const StyledMFAInputContainer = styled.div`
  display: flex;
  align-items: start;
  gap: 0.5em;
  flex-wrap: wrap;

  @media (min-width: 768px) {
    flex-wrap: nowrap;
  }
`;

const getStatus = (validated: boolean, mfa: string, codeLength: number): GoodBadOrUnknown => {
  if (validated) return 'good';
  if (mfa.length === codeLength) return 'bad';
  return 'unknown';
};

const MFAInput: React.FC<MFAInputProps> = ({
  codeLength,
  onCodeChanged,
  validated,
  disabled = false,
  clearUserInput,
  showState = true,
  resetErrorDisplay = () => {},
  resendButton = null,
  ...rest
}) => {
  const [mfa, setMfa] = useState('');

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const code = e.target.value;

    setMfa(code);

    if (code.length === codeLength) {
      onCodeChanged(code);
    } else {
      resetErrorDisplay();
    }
  };

  useEffect(() => {
    setMfa('');
  }, [clearUserInput]);

  const status = getStatus(validated, mfa, codeLength);

  return (
    <StyledMFAInputContainer>
      <Input
        {...rest}
        onChange={handleChange}
        value={mfa}
        disabled={disabled}
        maxLength={codeLength}
        suffix={showState && <Suffix color={statusMap.get(status)?.color}>{statusMap.get(status)?.character}</Suffix>}
        inputMode="numeric"
        autoComplete="one-time-code"
        data-testid="mfa-input"
      />

      {resendButton}
    </StyledMFAInputContainer>
  );
};

export default MFAInput;
